import type Database from "@tauri-apps/plugin-sql";
import { logger } from "@/services/logger";

const log = logger.scope("sqlite-schema");

export const SCHEMA_VERSION = 2;

/** Every core table, one full statement per array entry — `plugin-sql`'s
 * `execute()` runs a single statement per call, so these must never be
 * concatenated/split on `;` (that would break the trigger bodies below,
 * which contain semicolons of their own). */
const CORE_TABLES: readonly string[] = [
  `CREATE TABLE IF NOT EXISTS meta (
    key TEXT PRIMARY KEY,
    value TEXT NOT NULL
  )`,

  `CREATE TABLE IF NOT EXISTS conversations (
    id TEXT PRIMARY KEY,
    title TEXT NOT NULL,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL,
    model_id TEXT,
    params_json TEXT NOT NULL,
    title_generated INTEGER NOT NULL DEFAULT 0,
    knowledge_scope_json TEXT NOT NULL DEFAULT '{"mode":"none"}'
  )`,

  `CREATE TABLE IF NOT EXISTS messages (
    id TEXT PRIMARY KEY,
    conversation_id TEXT NOT NULL REFERENCES conversations(id) ON DELETE CASCADE,
    seq INTEGER NOT NULL,
    role TEXT NOT NULL,
    content TEXT NOT NULL,
    created_at INTEGER NOT NULL,
    status TEXT NOT NULL,
    error TEXT
  )`,
  `CREATE INDEX IF NOT EXISTS idx_messages_conversation ON messages(conversation_id, seq)`,

  `CREATE TABLE IF NOT EXISTS memories (
    id TEXT PRIMARY KEY,
    content TEXT NOT NULL,
    tags_json TEXT NOT NULL DEFAULT '[]',
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
  )`,

  `CREATE TABLE IF NOT EXISTS collections (
    id TEXT PRIMARY KEY,
    name TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )`,

  `CREATE TABLE IF NOT EXISTS documents (
    id TEXT PRIMARY KEY,
    collection_id TEXT REFERENCES collections(id) ON DELETE SET NULL,
    name TEXT NOT NULL,
    source_path TEXT NOT NULL,
    file_type TEXT NOT NULL,
    size_bytes INTEGER NOT NULL,
    imported_at INTEGER NOT NULL,
    status TEXT NOT NULL,
    error TEXT,
    tags_json TEXT NOT NULL DEFAULT '[]'
  )`,
  `CREATE INDEX IF NOT EXISTS idx_documents_collection ON documents(collection_id)`,

  `CREATE TABLE IF NOT EXISTS chunks (
    id TEXT PRIMARY KEY,
    document_id TEXT NOT NULL REFERENCES documents(id) ON DELETE CASCADE,
    seq INTEGER NOT NULL,
    content TEXT NOT NULL,
    embedding BLOB
  )`,
  `CREATE INDEX IF NOT EXISTS idx_chunks_document ON chunks(document_id, seq)`,

  `CREATE TABLE IF NOT EXISTS processing_queue (
    id TEXT PRIMARY KEY,
    document_id TEXT NOT NULL REFERENCES documents(id) ON DELETE CASCADE,
    status TEXT NOT NULL,
    attempts INTEGER NOT NULL DEFAULT 0,
    last_error TEXT,
    updated_at INTEGER NOT NULL
  )`,

  `CREATE TABLE IF NOT EXISTS recent_files (
    path TEXT PRIMARY KEY,
    opened_at INTEGER NOT NULL
  )`,

  // --- Phase 4: AI Workspace --------------------------------------------

  `CREATE TABLE IF NOT EXISTS workspaces (
    id TEXT PRIMARY KEY,
    name TEXT NOT NULL,
    icon TEXT NOT NULL DEFAULT 'Layers',
    color TEXT,
    default_model TEXT,
    ai_params_json TEXT NOT NULL DEFAULT '{}',
    is_default INTEGER NOT NULL DEFAULT 0,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
  )`,

  `CREATE TABLE IF NOT EXISTS conversation_folders (
    id TEXT PRIMARY KEY,
    workspace_id TEXT NOT NULL REFERENCES workspaces(id) ON DELETE CASCADE,
    name TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_conversation_folders_workspace ON conversation_folders(workspace_id)`,

  `CREATE TABLE IF NOT EXISTS note_folders (
    id TEXT PRIMARY KEY,
    workspace_id TEXT NOT NULL REFERENCES workspaces(id) ON DELETE CASCADE,
    parent_id TEXT REFERENCES note_folders(id) ON DELETE SET NULL,
    name TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_note_folders_workspace ON note_folders(workspace_id)`,

  `CREATE TABLE IF NOT EXISTS notes (
    id TEXT PRIMARY KEY,
    workspace_id TEXT NOT NULL REFERENCES workspaces(id) ON DELETE CASCADE,
    folder_id TEXT REFERENCES note_folders(id) ON DELETE SET NULL,
    title TEXT NOT NULL,
    content TEXT NOT NULL DEFAULT '',
    tags_json TEXT NOT NULL DEFAULT '[]',
    pinned INTEGER NOT NULL DEFAULT 0,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_notes_workspace ON notes(workspace_id)`,
  `CREATE INDEX IF NOT EXISTS idx_notes_folder ON notes(folder_id)`,

  `CREATE TABLE IF NOT EXISTS note_versions (
    id TEXT PRIMARY KEY,
    note_id TEXT NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
    content TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_note_versions_note ON note_versions(note_id, created_at)`,

  `CREATE TABLE IF NOT EXISTS note_links (
    id TEXT PRIMARY KEY,
    from_note_id TEXT NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
    to_note_id TEXT NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_note_links_from ON note_links(from_note_id)`,

  `CREATE TABLE IF NOT EXISTS note_attachments (
    id TEXT PRIMARY KEY,
    note_id TEXT NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
    file_name TEXT NOT NULL,
    file_path TEXT NOT NULL,
    mime_type TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_note_attachments_note ON note_attachments(note_id)`,

  `CREATE TABLE IF NOT EXISTS prompt_categories (
    id TEXT PRIMARY KEY,
    workspace_id TEXT NOT NULL REFERENCES workspaces(id) ON DELETE CASCADE,
    name TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_prompt_categories_workspace ON prompt_categories(workspace_id)`,

  `CREATE TABLE IF NOT EXISTS prompts (
    id TEXT PRIMARY KEY,
    workspace_id TEXT NOT NULL REFERENCES workspaces(id) ON DELETE CASCADE,
    category_id TEXT REFERENCES prompt_categories(id) ON DELETE SET NULL,
    title TEXT NOT NULL,
    body TEXT NOT NULL DEFAULT '',
    variables_json TEXT NOT NULL DEFAULT '[]',
    is_favorite INTEGER NOT NULL DEFAULT 0,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_prompts_workspace ON prompts(workspace_id)`,

  `CREATE TABLE IF NOT EXISTS projects (
    id TEXT PRIMARY KEY,
    workspace_id TEXT NOT NULL REFERENCES workspaces(id) ON DELETE CASCADE,
    name TEXT NOT NULL,
    description TEXT NOT NULL DEFAULT '',
    tags_json TEXT NOT NULL DEFAULT '[]',
    archived INTEGER NOT NULL DEFAULT 0,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_projects_workspace ON projects(workspace_id)`,

  `CREATE TABLE IF NOT EXISTS project_links (
    id TEXT PRIMARY KEY,
    project_id TEXT NOT NULL REFERENCES projects(id) ON DELETE CASCADE,
    entity_type TEXT NOT NULL CHECK (entity_type IN ('conversation', 'note', 'prompt', 'task', 'knowledge_document', 'file')),
    entity_id TEXT NOT NULL,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_project_links_project ON project_links(project_id)`,
  `CREATE INDEX IF NOT EXISTS idx_project_links_entity ON project_links(entity_type, entity_id)`,

  `CREATE TABLE IF NOT EXISTS tasks (
    id TEXT PRIMARY KEY,
    workspace_id TEXT NOT NULL REFERENCES workspaces(id) ON DELETE CASCADE,
    project_id TEXT REFERENCES projects(id) ON DELETE SET NULL,
    conversation_id TEXT REFERENCES conversations(id) ON DELETE SET NULL,
    title TEXT NOT NULL,
    notes TEXT NOT NULL DEFAULT '',
    due_at INTEGER,
    priority TEXT NOT NULL DEFAULT 'normal',
    completed INTEGER NOT NULL DEFAULT 0,
    completed_at INTEGER,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_tasks_workspace ON tasks(workspace_id)`,
  `CREATE INDEX IF NOT EXISTS idx_tasks_project ON tasks(project_id)`,

  `CREATE TABLE IF NOT EXISTS clipboard_items (
    id TEXT PRIMARY KEY,
    content TEXT NOT NULL,
    content_type TEXT NOT NULL DEFAULT 'text',
    pinned INTEGER NOT NULL DEFAULT 0,
    created_at INTEGER NOT NULL
  )`,
  `CREATE INDEX IF NOT EXISTS idx_clipboard_items_created ON clipboard_items(created_at)`,
];

/** Individual `ALTER TABLE ... ADD COLUMN` statements layering Phase 4
 * columns onto tables that existed before it. Unlike `CORE_TABLES`, these
 * can't use `IF NOT EXISTS` — SQLite has no such clause for `ADD COLUMN` —
 * so each one runs in its own try/catch in `runMigrations`, treating a
 * "duplicate column" failure as "already applied" rather than an error. */
const SCHEMA_ALTERATIONS: readonly string[] = [
  `ALTER TABLE conversations ADD COLUMN workspace_id TEXT REFERENCES workspaces(id) ON DELETE SET NULL`,
  `ALTER TABLE conversations ADD COLUMN pinned INTEGER NOT NULL DEFAULT 0`,
  `ALTER TABLE conversations ADD COLUMN folder_id TEXT REFERENCES conversation_folders(id) ON DELETE SET NULL`,
  `ALTER TABLE conversations ADD COLUMN parent_conversation_id TEXT REFERENCES conversations(id) ON DELETE SET NULL`,
  `ALTER TABLE conversations ADD COLUMN branch_point_seq INTEGER`,
  `ALTER TABLE collections ADD COLUMN workspace_id TEXT REFERENCES workspaces(id) ON DELETE SET NULL`,
  `ALTER TABLE messages ADD COLUMN favorite INTEGER NOT NULL DEFAULT 0`,
  `ALTER TABLE messages ADD COLUMN images_json TEXT NOT NULL DEFAULT '[]'`,
];

/** FTS5 virtual tables + the external-content sync triggers that keep them
 * current. Kept separate from `CORE_TABLES` because FTS5 support depends on
 * how the host's bundled SQLite was compiled — `runMigrations` treats a
 * failure here as non-fatal and falls back to `LIKE`-based search (see
 * `services/search/searchService.ts`). */
const FTS_STATEMENTS: readonly string[] = [
  `CREATE VIRTUAL TABLE IF NOT EXISTS conversations_fts USING fts5(
    title, content='conversations', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS conversations_ai AFTER INSERT ON conversations BEGIN
    INSERT INTO conversations_fts(rowid, title) VALUES (new.rowid, new.title);
  END`,
  `CREATE TRIGGER IF NOT EXISTS conversations_ad AFTER DELETE ON conversations BEGIN
    INSERT INTO conversations_fts(conversations_fts, rowid, title) VALUES ('delete', old.rowid, old.title);
  END`,
  `CREATE TRIGGER IF NOT EXISTS conversations_au AFTER UPDATE ON conversations BEGIN
    INSERT INTO conversations_fts(conversations_fts, rowid, title) VALUES ('delete', old.rowid, old.title);
    INSERT INTO conversations_fts(rowid, title) VALUES (new.rowid, new.title);
  END`,

  `CREATE VIRTUAL TABLE IF NOT EXISTS messages_fts USING fts5(
    content, content='messages', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS messages_ai AFTER INSERT ON messages BEGIN
    INSERT INTO messages_fts(rowid, content) VALUES (new.rowid, new.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS messages_ad AFTER DELETE ON messages BEGIN
    INSERT INTO messages_fts(messages_fts, rowid, content) VALUES ('delete', old.rowid, old.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS messages_au AFTER UPDATE ON messages BEGIN
    INSERT INTO messages_fts(messages_fts, rowid, content) VALUES ('delete', old.rowid, old.content);
    INSERT INTO messages_fts(rowid, content) VALUES (new.rowid, new.content);
  END`,

  `CREATE VIRTUAL TABLE IF NOT EXISTS chunks_fts USING fts5(
    content, content='chunks', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS chunks_ai AFTER INSERT ON chunks BEGIN
    INSERT INTO chunks_fts(rowid, content) VALUES (new.rowid, new.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS chunks_ad AFTER DELETE ON chunks BEGIN
    INSERT INTO chunks_fts(chunks_fts, rowid, content) VALUES ('delete', old.rowid, old.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS chunks_au AFTER UPDATE ON chunks BEGIN
    INSERT INTO chunks_fts(chunks_fts, rowid, content) VALUES ('delete', old.rowid, old.content);
    INSERT INTO chunks_fts(rowid, content) VALUES (new.rowid, new.content);
  END`,

  `CREATE VIRTUAL TABLE IF NOT EXISTS notes_fts USING fts5(
    title, content, content='notes', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS notes_ai AFTER INSERT ON notes BEGIN
    INSERT INTO notes_fts(rowid, title, content) VALUES (new.rowid, new.title, new.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS notes_ad AFTER DELETE ON notes BEGIN
    INSERT INTO notes_fts(notes_fts, rowid, title, content) VALUES ('delete', old.rowid, old.title, old.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS notes_au AFTER UPDATE ON notes BEGIN
    INSERT INTO notes_fts(notes_fts, rowid, title, content) VALUES ('delete', old.rowid, old.title, old.content);
    INSERT INTO notes_fts(rowid, title, content) VALUES (new.rowid, new.title, new.content);
  END`,

  `CREATE VIRTUAL TABLE IF NOT EXISTS prompts_fts USING fts5(
    title, body, content='prompts', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS prompts_ai AFTER INSERT ON prompts BEGIN
    INSERT INTO prompts_fts(rowid, title, body) VALUES (new.rowid, new.title, new.body);
  END`,
  `CREATE TRIGGER IF NOT EXISTS prompts_ad AFTER DELETE ON prompts BEGIN
    INSERT INTO prompts_fts(prompts_fts, rowid, title, body) VALUES ('delete', old.rowid, old.title, old.body);
  END`,
  `CREATE TRIGGER IF NOT EXISTS prompts_au AFTER UPDATE ON prompts BEGIN
    INSERT INTO prompts_fts(prompts_fts, rowid, title, body) VALUES ('delete', old.rowid, old.title, old.body);
    INSERT INTO prompts_fts(rowid, title, body) VALUES (new.rowid, new.title, new.body);
  END`,

  `CREATE VIRTUAL TABLE IF NOT EXISTS projects_fts USING fts5(
    name, description, content='projects', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS projects_ai AFTER INSERT ON projects BEGIN
    INSERT INTO projects_fts(rowid, name, description) VALUES (new.rowid, new.name, new.description);
  END`,
  `CREATE TRIGGER IF NOT EXISTS projects_ad AFTER DELETE ON projects BEGIN
    INSERT INTO projects_fts(projects_fts, rowid, name, description) VALUES ('delete', old.rowid, old.name, old.description);
  END`,
  `CREATE TRIGGER IF NOT EXISTS projects_au AFTER UPDATE ON projects BEGIN
    INSERT INTO projects_fts(projects_fts, rowid, name, description) VALUES ('delete', old.rowid, old.name, old.description);
    INSERT INTO projects_fts(rowid, name, description) VALUES (new.rowid, new.name, new.description);
  END`,

  `CREATE VIRTUAL TABLE IF NOT EXISTS tasks_fts USING fts5(
    title, notes, content='tasks', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS tasks_ai AFTER INSERT ON tasks BEGIN
    INSERT INTO tasks_fts(rowid, title, notes) VALUES (new.rowid, new.title, new.notes);
  END`,
  `CREATE TRIGGER IF NOT EXISTS tasks_ad AFTER DELETE ON tasks BEGIN
    INSERT INTO tasks_fts(tasks_fts, rowid, title, notes) VALUES ('delete', old.rowid, old.title, old.notes);
  END`,
  `CREATE TRIGGER IF NOT EXISTS tasks_au AFTER UPDATE ON tasks BEGIN
    INSERT INTO tasks_fts(tasks_fts, rowid, title, notes) VALUES ('delete', old.rowid, old.title, old.notes);
    INSERT INTO tasks_fts(rowid, title, notes) VALUES (new.rowid, new.title, new.notes);
  END`,

  `CREATE VIRTUAL TABLE IF NOT EXISTS clipboard_items_fts USING fts5(
    content, content='clipboard_items', content_rowid='rowid'
  )`,
  `CREATE TRIGGER IF NOT EXISTS clipboard_items_ai AFTER INSERT ON clipboard_items BEGIN
    INSERT INTO clipboard_items_fts(rowid, content) VALUES (new.rowid, new.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS clipboard_items_ad AFTER DELETE ON clipboard_items BEGIN
    INSERT INTO clipboard_items_fts(clipboard_items_fts, rowid, content) VALUES ('delete', old.rowid, old.content);
  END`,
  `CREATE TRIGGER IF NOT EXISTS clipboard_items_au AFTER UPDATE ON clipboard_items BEGIN
    INSERT INTO clipboard_items_fts(clipboard_items_fts, rowid, content) VALUES ('delete', old.rowid, old.content);
    INSERT INTO clipboard_items_fts(rowid, content) VALUES (new.rowid, new.content);
  END`,
];

let ftsAvailable: boolean | null = null;

/** Whether FTS5 virtual tables were successfully created on this
 * connection — `searchService` uses this to decide between an FTS5 query
 * and a `LIKE` fallback. Only meaningful after `runMigrations` has run. */
export function isFtsAvailable(): boolean {
  return ftsAvailable ?? false;
}

export async function runMigrations(db: Database): Promise<void> {
  for (const statement of CORE_TABLES) {
    await db.execute(statement);
  }

  for (const statement of SCHEMA_ALTERATIONS) {
    try {
      await db.execute(statement);
    } catch (err) {
      // Already applied on a prior run — SQLite has no `ADD COLUMN IF NOT
      // EXISTS`, so a "duplicate column name" failure here just means this
      // alteration already landed. Anything else is logged but non-fatal,
      // consistent with the FTS5-availability handling below.
      const message = err instanceof Error ? err.message : String(err);
      if (!/duplicate column name/i.test(message)) {
        log.warn(`Schema alteration failed (may be pre-existing): ${statement}`, err);
      }
    }
  }

  try {
    for (const statement of FTS_STATEMENTS) {
      await db.execute(statement);
    }
    ftsAvailable = true;
  } catch (err) {
    ftsAvailable = false;
    log.warn(
      "FTS5 isn't available in this SQLite build; falling back to LIKE-based search.",
      err,
    );
  }

  await ensureDefaultWorkspace(db);

  await db.execute("INSERT OR REPLACE INTO meta (key, value) VALUES ('schema_version', $1)", [
    String(SCHEMA_VERSION),
  ]);
}

/** Guarantees at least one workspace exists — every Phase 4 entity is
 * workspace-scoped, so a brand-new database (or one upgrading from Phase 3)
 * needs a "Personal" workspace to attach existing/new records to. Cheap and
 * idempotent: only inserts when the table is empty. */
async function ensureDefaultWorkspace(db: Database): Promise<void> {
  const rows = await db.select<{ count: number }[]>("SELECT COUNT(*) AS count FROM workspaces");
  if ((rows[0]?.count ?? 0) > 0) return;
  const now = Date.now();
  await db.execute(
    `INSERT INTO workspaces (id, name, icon, color, default_model, ai_params_json, is_default, created_at, updated_at)
     VALUES ('workspace_default', 'Personal', 'Layers', NULL, NULL, '{}', 1, $1, $1)`,
    [now],
  );
}
